import React, { useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import AccordionGallery from '../components/AccordionGallery';
import { caseStudies } from '../mockData';
import { trackEvent } from '@/lib/tracking';

export const Portfolio = () => {
  // Map each case study to an AccordionGallery item. We use the top-2 result
  // metrics as the overlay stats and keep the client / title / industry for
  // the caption bar so all existing content stays intact.
  const galleryItems = caseStudies.map((cs) => ({
    to: `/portfolio/${cs.slug || cs.id}`,
    image: cs.image,
    alt: cs.client,
    industry: cs.industry,
    label: cs.client,
    subtitle: cs.title,
    testId: `portfolio-card-${cs.slug || cs.id}`,
    stats: (cs.results || []).slice(0, 2).map((r) => ({
      value: r.value,
      label: r.metric,
    })),
  }));

  // Featured case study opens by default so the gallery is never "dead".
  const defaultIndex = Math.max(
    0,
    caseStudies.findIndex((cs) => cs.featured)
  );

  return (
    <div className="portfolio-page pt-20">
      {/* Hero Section */}
      <section className="portfolio-hero py-24 bg-gradient-to-br from-black via-gray-900 to-black relative overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="font-display text-6xl font-bold text-white mb-6">
              Proof, Not Promises:<br />
              <span className="text-gradient">Our Work Speaks</span>
            </h1>
            <p className="text-xl text-gray-400 leading-relaxed">
              Real clients. Real challenges. Real results. See how we've helped ambitious businesses connect strategy to growth.
            </p>
          </div>
        </div>
      </section>

      {/* Accordion Gallery — desktop only. Mobile gets a stacked cards view. */}
      <section className="case-studies-gallery py-16 md:py-24 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="hidden md:block">
            <AccordionGallery
              items={galleryItems}
              defaultIndex={defaultIndex}
              accentColor="#EF4444"
              overlayColor="#0a1224"
              textColor="#ffffff"
              height={560}
              gap={12}
              radius={18}
              expandRatio={0.5}
              trigger="hover"
              grayscale
            />
            <p className="mt-6 text-sm text-muted-foreground text-center" data-testid="gallery-hint">
              Hover a panel to preview · click again to open the case study
            </p>
          </div>

          {/* Mobile-only stacked case study cards */}
          <div className="md:hidden grid gap-6" data-testid="portfolio-mobile-stack">
            {caseStudies.map((cs) => (
              <Link
                to={`/portfolio/${cs.slug || cs.id}`}
                key={cs.id}
                className="block rounded-2xl overflow-hidden bg-card border border-border/70 hover:border-primary/60 transition-colors"
                data-testid={`portfolio-mobile-card-${cs.slug || cs.id}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={cs.image} alt={cs.client} className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-red-600/95 text-white text-[10px] font-medium rounded-full tracking-[0.2em] uppercase">
                      {cs.industry}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-display font-semibold text-2xl text-white leading-tight">
                      {cs.client}
                    </h3>
                    <p className="font-sans text-sm text-white/85 mt-1 line-clamp-2">
                      {cs.title}
                    </p>
                  </div>
                </div>
                {Array.isArray(cs.results) && cs.results.length > 0 && (
                  <div className="grid grid-cols-2 gap-3 p-4">
                    {cs.results.slice(0, 2).map((r, idx) => (
                      <div
                        key={idx}
                        className="rounded-lg p-3 bg-secondary/40 border border-border/50"
                      >
                        <div className="font-display font-bold text-lg text-foreground leading-none">
                          {r.value}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-primary mt-1.5">
                          {r.metric}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <div className="px-4 pb-4 flex items-center gap-1.5 text-primary text-sm font-medium">
                  Read case study
                  <ArrowRight className="w-4 h-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export const CaseStudyDetail = () => {
  const { id } = useParams();
  const study = caseStudies.find(cs => cs.slug === id || cs.id === parseInt(id));

  // Fire view_case_study exactly once per slug mount. useRef prevents refires
  // on re-render or on a parent state change while still refiring when the
  // user navigates between two case studies (slug changes → ref resets).
  const firedFor = useRef(null);
  const currentSlug = study ? (study.slug || String(study.id)) : null;
  useEffect(() => {
    if (!currentSlug) return;
    if (firedFor.current === currentSlug) return;
    firedFor.current = currentSlug;
    try {
      trackEvent('view_case_study', { case_study_slug: currentSlug });
    } catch {
      /* tracking never breaks the user flow */
    }
  }, [currentSlug]);

  if (!study) {
    return <div className="pt-20 min-h-screen flex items-center justify-center text-white">Case study not found</div>;
  }

  // SEO + GEO/AEO schema (CreativeWork article + organisation publisher)
  const detailUrl = `https://radiusconsulting.co.za/portfolio/${study.slug || study.id}`;
  const seoTitle = `${study.client}: ${study.title}`;
  const seoDescription = study.summary || study.challenge;
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CreativeWork",
        "@id": `${detailUrl}#case-study`,
        "name": seoTitle,
        "headline": seoTitle,
        "description": seoDescription,
        "image": study.image,
        "url": detailUrl,
        "about": study.industry,
        "keywords": [
          study.client,
          study.industry,
          study.title,
          ...(study.practices || [])
        ].join(", "),
        "author": {
          "@type": "Organization",
          "name": "RADIUS CONSULTING",
          "url": "https://radiusconsulting.co.za"
        },
        "publisher": {
          "@type": "Organization",
          "name": "RADIUS CONSULTING",
          "logo": {
            "@type": "ImageObject",
            "url": "https://radiusconsulting.co.za/logo.png"
          }
        }
      }
    ]
  };

  return (
    <div className="case-study-detail pt-20" data-testid="case-study-detail">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={[study.client, study.industry, study.title, ...(study.practices || [])].join(", ")}
        image={study.image}
        url={detailUrl}
        type="article"
        article={true}
        schema={schema}
      />
      {/* Hero Section */}
      <section className="detail-hero relative h-[60vh] overflow-hidden">
        <img 
          src={study.image} 
          alt={study.client}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
        
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 w-full">
            <Link to="/portfolio" className="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors">
              <ArrowLeft className="mr-2 w-5 h-5" />
              Back to Our Work
            </Link>
            <span className="inline-block px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-full mb-6">
              {study.industry}
            </span>
            <h1 className="text-6xl font-bold text-white mb-4">{study.client}</h1>
            <p className="text-2xl text-gray-300">{study.title}</p>
          </div>
        </div>
      </section>

      {/* Results Overview */}
      <section className="results-overview py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
            {study.results.map((result, idx) => (
              <Card key={idx} className="result-card text-center">
                <CardContent className="p-8">
                  <div className="text-4xl font-bold text-gradient mb-3 break-words">{result.value}</div>
                  <div className="text-gray-400 text-sm uppercase tracking-wider">{result.metric}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Challenge & Solution */}
      <section className="challenge-solution py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">The Challenge</h2>
              <p className="text-xl text-gray-400 leading-relaxed">{study.challenge}</p>
            </div>
            <div>
              <h2 className="text-4xl font-bold text-white mb-6">Our Solution</h2>
              <p className="text-xl text-gray-400 leading-relaxed">{study.solution}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {study.testimonial && (
        <section className="testimonial py-20 bg-black">
          <div className="max-w-4xl mx-auto px-6">
            <Card className="testimonial-card">
              <CardContent className="p-12 text-center">
                <div className="text-6xl text-red-500 mb-6">"</div>
                <p className="text-2xl text-gray-300 mb-8 leading-relaxed italic">
                  {study.testimonial.quote}
                </p>
                <div className="text-white font-semibold text-lg">{study.testimonial.author}</div>
                <div className="text-gray-500">{study.testimonial.role}</div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      {/* Extended Narrative - only for studies with fullCaseStudy data */}
      {study.fullCaseStudy && (
        <section className="extended-narrative py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-6">
            {/* Background */}
            {study.fullCaseStudy.background && (
              <div className="mb-16">
                <h2 className="text-4xl font-bold text-white mb-6">Background</h2>
                <p className="text-xl text-gray-400 leading-relaxed">
                  {study.fullCaseStudy.background}
                </p>
              </div>
            )}

            {/* What We Did */}
            {study.fullCaseStudy.whatWeDid && study.fullCaseStudy.whatWeDid.length > 0 && (
              <div className="mb-16">
                <h2 className="text-4xl font-bold text-white mb-8">What Radius Did</h2>
                <div className="space-y-6">
                  {study.fullCaseStudy.whatWeDid.map((item, idx) => (
                    <div key={idx} className="flex gap-4">
                      <div className="text-red-500 text-xl flex-shrink-0">—</div>
                      <p className="text-lg text-gray-400 leading-relaxed">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Results in Detail */}
            {study.fullCaseStudy.resultsDetail && (
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Results in Detail</h2>
                <p className="text-xl text-gray-400 leading-relaxed">
                  {study.fullCaseStudy.resultsDetail}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Related Work — pulls 3 cards: same industry first, then same practice, then fill */}
      {(() => {
        const others = caseStudies.filter(cs => (cs.slug || String(cs.id)) !== (study.slug || String(study.id)));
        const sameIndustry = others.filter(cs => cs.industry === study.industry);
        const studyPractices = study.practices || [];
        const samePractice = others.filter(
          cs => !sameIndustry.includes(cs) && (cs.practices || []).some(p => studyPractices.includes(p))
        );
        const remaining = others.filter(cs => !sameIndustry.includes(cs) && !samePractice.includes(cs));
        const related = [...sameIndustry, ...samePractice, ...remaining].slice(0, 3);

        if (related.length === 0) return null;

        return (
          <section className="related-work py-20 bg-gray-900" data-testid="related-work">
            <div className="max-w-7xl mx-auto px-6">
              <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
                <h2 className="text-4xl font-bold text-white">Related Work</h2>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center text-red-500 font-medium hover:gap-2 transition-all"
                  data-testid="related-work-view-all"
                >
                  View all
                  <ArrowRight className="ml-1 w-5 h-5" />
                </Link>
              </div>

              <div className="grid md:grid-cols-3 gap-8">
                {related.map((rel) => (
                  <Link
                    to={`/portfolio/${rel.slug || rel.id}`}
                    key={rel.id}
                    data-testid={`related-card-${rel.slug || rel.id}`}
                  >
                    <Card className="case-study-card group overflow-hidden h-full hover-lift">
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={rel.image}
                          alt={rel.client}
                          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
                        <div className="absolute top-4 right-4">
                          <span className="px-3 py-1 bg-red-600/90 text-white text-xs font-medium rounded-full">
                            {rel.industry}
                          </span>
                        </div>
                      </div>
                      <CardContent className="p-6">
                        <h3 className="text-2xl font-bold text-white mb-2 group-hover:text-gradient transition-all">
                          {rel.client}
                        </h3>
                        <p className="text-base text-gray-400 mb-4">{rel.title}</p>
                        <div className="flex items-center text-red-500 font-medium text-sm group-hover:gap-2 transition-all">
                          View Case Study
                          <ArrowRight className="ml-1 w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* CTA Section */}
      <section className="cta-section py-24 bg-gradient-to-br from-red-600/5 to-transparent">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-5xl font-bold text-white mb-6">
            Want Similar Results?
          </h2>
          <p className="text-xl text-gray-400 mb-12">
            Let's discuss how we can help your business achieve breakthrough growth.
          </p>
          <Link to="/contact">
            <Button size="lg" className="btn-primary group">
              Connect
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Portfolio;
